/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Avatar, Box, Breadcrumbs, Typography, Link, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid } from '@mui/material'
import { useState, useEffect } from 'react'
import { canCreateChild, type lookup, type postError } from 'core/types'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { Column, ColumnSort, ColumnType, type RowData } from 'Components/Table/DataTable/DataTable.model'
import DataTable from 'Components/Table/DataTable'
import { EnrollmentSearch, type EnrollmentSearchCriteria } from 'Pages/ServiceProvider/Enrollments/EnrollmentSearch'
import { MenuButton, type MenuButtonOption } from 'Components/Menu/MenuButton'
import { toast } from 'react-toastify'
import { ErrorDisplay } from 'ContextProviders/ErrorProvider'
import { type SelectOption, SelectWithLabel } from 'Components/SelectWithLabel'
import { useParams, generatePath, useLocation } from 'react-router'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { type PaginationProps, type SearchCriteria, usePagination } from 'hooks/use-pagination'
import { EnrollmentChip } from 'Components/Enrollment/EnrollmentChip'
import S3Avatar from 'Components/S3Image/S3Avatar'
import { colorKeys } from 'Components/Enrollment/Colors'
import { ProgramVerificationChip } from 'Components/Program/ProgramVerificationChip'
import { ENROLLMENT } from 'Pages/Routes'
import { useNavigate } from 'react-router-dom'
import { NewChildValuesModal } from 'Components/Enrollment/NewChildValuesModal'
import { FormatDateTimeValue } from 'core/Utilities'
import { usePermissions } from 'hooks/use-permissions'

enum ActionType {
  MassEnroll = 'MassEnroll',
  MassExit = 'MassExit',
  MassStart = 'MassStart'
}

const nameColumn = new Column('childName', 'Student Name', ColumnType.CUSTOM)
nameColumn.customCellGenerator = row => {
  return <div style={{ whiteSpace: 'nowrap' }} >
    {row.imageKey === null
      ? <Avatar sx={{ backgroundColor: '#7136BC', display: 'inline-grid' }}>{String(row.childName)[0]}</Avatar>
      : <S3Avatar imageKey={row.imageKey} height='50px' width='50px' style={{ display: 'inline-grid' }} />}
    <span style={{ whiteSpace: 'nowrap', paddingLeft: '5px' }}>{row.childName}</span>
  </div>
}
const facilityColumn = new Column('facilityName', 'Facility')
const schoolYearColumn = new Column('schoolYear', 'School Year')

const statusColumn = new Column('status', 'Status', ColumnType.CUSTOM)
statusColumn.customCellGenerator = row => {
  return <div>
    <EnrollmentChip statusId={row.status} />
  </div>
}
const ageColumn = new Column('dateOfBirth', 'Age\r\nAge 9/1', ColumnType.CUSTOM)
ageColumn.customCellGenerator = row => {
  return <>
    {row.yearsMonthsAge != null && <div style={{ width: '80px' }} >
      {row.yearsMonthsAge}
    </div>
    }
    {row.yearsMonthsAgeAsOfSept1 != null && <div>
      {row.yearsMonthsAgeAsOfSept1}
    </div>
    }
  </>
}

const programColumn = new Column('programName', 'Program', ColumnType.CUSTOM)
programColumn.customCellGenerator = row => {
  return <ProgramVerificationChip programName={row.programName} status={row.programVerificationStatus} showStatusName={false} compressed={true} />
}

const sessionColumn = new Column('sessionName', 'Session')

const povertyColumn = new Column('povertyPercentage', 'FPL %', ColumnType.CUSTOM)
povertyColumn.customCellGenerator = row => {
  const povertyPercentage = row.povertyPercentage as number | undefined
  return <div>
    {povertyPercentage != null && `${povertyPercentage}%`}
  </div>
}
const districtColumn = new Column('residentDistrict', 'Resident District')

const lastActionColumn = new Column('lastAction', 'Last Action', ColumnType.CUSTOM)
lastActionColumn.customCellGenerator = row => {
  return <div>{FormatDateTimeValue(row.lastAction)}</div>
}

export function EnrollmentList (): JSX.Element {
  const { serviceId } = useParams()
  const nav = useNavigate()
  const act = useAccountId()
  const accountId = act?.id ?? 0
  const [exitReasonId, setExitReasonId] = useState<number | undefined>(undefined)
  const [actionDate, setActionDate] = useState<Date | undefined>(undefined)
  const [deliveryScheduleId, setDeliveryScheduleId] = useState<number | undefined>(undefined)
  const [showCheckBox, setShowCheckbox] = useState<boolean>(false)
  const [enrollments, setEnrollments] = useState<RowData[]>([])
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false)
  const [massEnrollmentList, setMassEnrollmentList] = useState<number[]>([])
  const [scheduleList, setScheduleList] = useState<Array<{ id: number, name: string }>>([])
  const [exitReasons, setExitReasons] = useState<lookup[]>([])
  const [showAddChild, setShowAddChild] = useState(false)
  const [currentAction, setCurrentAction] = useState<ActionType | undefined>(undefined)
  const [enrollmentName, setEnrollmentName] = useState('')
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [activeItem, setActiveItem] = useState<number | undefined>(undefined)
  const [warning, setWarning] = useState<string>('')
  const [refresh, setRefresh] = useState(Date.now())
  const searchStorageKey = `${accountId}enrollmentSearchData`
  const location = useLocation()
  const returnText: string | null = location.state?.redirectText ?? null
  const perms = usePermissions()

  const storedSearch = window.localStorage.getItem(searchStorageKey)
  let initialSearch: EnrollmentSearchCriteria
  if (storedSearch !== null) {
    initialSearch = JSON.parse(storedSearch)
  } else {
    initialSearch = {
      statuses: [colorKeys[1], colorKeys[2], colorKeys[3], colorKeys[4], colorKeys[5], colorKeys[6], colorKeys[7]],
      childName: '',
      facility: undefined,
      schoolYear: undefined,
      session: undefined,
      program: undefined
    }
  }
  const [enrollmentSearchData, setEnrollmentSearchData] = useState<EnrollmentSearchCriteria>(initialSearch)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const serviceResp = await sendGet(`/SessionList/GetService?serviceId=${serviceId ?? ''}`, {})
      if (serviceResp.success) {
        setEnrollmentName(serviceResp.response.name)
      }
      const { response: schedList } = await sendGet('/Enrollment/GetDeliverySchedules', {})
      setScheduleList(schedList)

      const { response: exitReasons } = await sendGet('/EnrollmentSearch/GetExitReasons', {})
      if (exitReasons !== null) {
        setExitReasons(exitReasons)
      }
    }
    handleFilter(initialSearch)
    void fetchData()
  }, [])

  const paginationProps: PaginationProps<RowData> = {
    rows: enrollments,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)

  const handleFilter = (newdata: EnrollmentSearchCriteria): void => {
    window.localStorage.setItem(searchStorageKey, JSON.stringify(newdata))

    setEnrollmentSearchData(newdata)
    const fieldArray: SearchCriteria[] = []
    if (newdata.childName !== '') {
      fieldArray.push({
        field: 'childName',
        value: newdata.childName
      })
    }
    if (newdata.schoolYear !== 0 && newdata.schoolYear !== undefined) {
      fieldArray.push({
        field: 'schoolYearID',
        value: newdata.schoolYear.toString(),
        exact: true
      })
    }
    if (newdata.session !== 0 && newdata.session !== undefined) {
      fieldArray.push({
        field: 'sessionID',
        value: newdata.session.toString(),
        exact: true
      })
    }
    if (newdata.program !== 0 && newdata.program !== undefined) {
      fieldArray.push({
        field: 'programID',
        value: newdata.program.toString(),
        exact: true
      })
    }
    if (newdata.facility !== 0 && newdata.facility !== undefined) {
      fieldArray.push({
        field: 'facilityID',
        value: newdata.facility.toString(),
        exact: true
      })
    }

    if (newdata.statuses.length > 0) {
      fieldArray.push({
        field: 'status',
        value: '',
        valueArray: newdata.statuses.map(status => colorKeys.indexOf(status))
      })
    }

    pagination.setSearchFields(fieldArray)
  }

  const columns = [nameColumn, schoolYearColumn, statusColumn, lastActionColumn, ageColumn, facilityColumn, programColumn, sessionColumn, povertyColumn, districtColumn]

  const exitDialogContent = <>
    <Box sx={{ margin: '20px' }}>
      <ErrorDisplay fieldName='Exit' />
      <SelectWithLabel
        width='100%'
        label='Exit Reason'
        name='exitReasonId'
        onChange={(evt: any) => { setExitReasonId(evt.target.value) }}
        options={exitReasons?.map(x => ({ name: x.name, id: x.id } satisfies SelectOption<number>)) ?? []}
        value={exitReasonId ?? ''}
      />
      <DatePickerWithLabel
        label='Exit Date'
        name='exitDate'
        onChange={(date: Date | null) => { setActionDate(date ?? undefined) }}
        value={actionDate ?? null}
      />
    </Box>
  </>

  const massStartDialogContent = <>
    <Box sx={{ margin: '20px' }}>
      <ErrorDisplay fieldName='Start' />
      <DatePickerWithLabel
        label='Start Date'
        name='startDate'
        onChange={(date: Date | null) => { setActionDate(date ?? undefined) }}
        value={actionDate ?? null}
      />
    </Box>
  </>

  const deliveryScheduleSelection = <SelectWithLabel
    width='75%'
    label='Delivery Schedule'
    name='deliveryScheduleId'
    onChange={(evt: any) => { setDeliveryScheduleId(evt.target.value) }}
    options={scheduleList}
    value={deliveryScheduleId ?? ''}
  />

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response } = await sendGet(`/NewEnrollment/SearchEnrollments?serviceID=${serviceId ?? 0}&spId=${accountId}`, {})
      if (response != null) {
        setEnrollments(response)
      }
    }
    void fetchData()
  }, [serviceId, accountId, refresh])

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    if (pagination.internalRows !== undefined && pagination.internalRows.length > 0) {
      const enrollmentIds: number[] = []
      if (checked) {
        pagination.internalRows.forEach((enrollment: RowData) => {
          enrollmentIds.push(enrollment.id)
        })
      }
      setMassEnrollmentList(enrollmentIds)
    }
  }

  const handleRawCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean, row: RowData): void => {
    if (massEnrollmentList !== undefined) {
      const index = massEnrollmentList?.findIndex(e => e === row.id)
      if (checked) {
        if (index === -1) {
          setMassEnrollmentList([...massEnrollmentList, row.id])
        }
      } else {
        setMassEnrollmentList(massEnrollmentList.filter(e => e !== row.id))
      }
    }
  }

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    const enrollmentId: string = row.id
    const enrollmentDetail = generatePath(ENROLLMENT, { id: enrollmentId })
    nav(enrollmentDetail)
  }

  const handleConfirmMassEnrollment = async (): Promise<boolean> => {
    if (deliveryScheduleId === undefined) {
      toast.error('Please select a delivery schedule first')
    } else if (!isRequestLoading) {
      setIsRequestLoading(true)

      const { success, response } = await sendPost('/Enrollment/MassEnrollment', { EnrollmentIds: massEnrollmentList ?? [], deliveryScheduleId })
      if (success && (response as boolean)) {
        setShowCheckbox(false)
        setMassEnrollmentList([])
        toast.success('Mass Enrollment Completed')
        setRefresh(Date.now())
      } else {
        toast.error('Mass Enrollment failed.  Be sure that all selected items have a session assigned')
      }
      setIsRequestLoading(false)
      return success
    }
    return false
  }

  const handleConfirmMassStart = async (): Promise<boolean> => {
    if (actionDate === undefined) {
      toast.error('Please select a Start Date first')
    } else if (!isRequestLoading) {
      setIsRequestLoading(true)
      const data = {
        enrollmentIds: massEnrollmentList,
        startDate: actionDate
      }
      const { response } = await sendPost('/Enrollment/MassStart', data)
      const rsp: { response: any, errors: postError[], success: boolean } = response
      if (rsp.success) {
        toast.success('Mass Start Completed')
        setShowCheckbox(false)
        setRefresh(Date.now())
      } else {
        rsp.errors.forEach(error => {
          toast.error(error.error)
        })
      }
      setIsRequestLoading(false)
      return rsp.success
    }
    return false
  }

  const handleConfirmMassExit = async (): Promise<boolean> => {
    if (!isRequestLoading) {
      setIsRequestLoading(true)
      const data = {
        enrollmentIds: massEnrollmentList,
        exitReasonId,
        exitDate: actionDate
      }
      const { response } = await sendPost('/Enrollment/MassExit', data)
      const rsp: { response: any, errors: postError[], success: boolean } = response
      if (rsp.success) {
        toast.success('Mass Exit Completed')
        setShowCheckbox(false)
        setRefresh(Date.now())
      } else {
        rsp.errors.forEach(error => {
          toast.error(error.error)
        })
      }
      setIsRequestLoading(false)
      return rsp.success
    }
    return false
  }

  const showCheckboxes = (type: ActionType): void => {
    setCurrentAction(type)
    setShowCheckbox(true)
  }

  const hideCheckboxes = (): void => {
    setMassEnrollmentList([])
    setCurrentAction(undefined)
    setShowCheckbox(false)
  }

  const handleContinueClick = (index: number): void => {
    setShowConfirmation(true)
    setActiveItem(index)
    const str = menuButtonOptions?.[index].warning ?? ''
    setWarning(str)
  }

  const handleConfirm = async (): Promise<boolean | undefined> => {
    const item = menuButtonOptions?.[activeItem ?? 0]
    if (item?.onConfirm !== undefined) {
      const success = await item.onConfirm() ?? false
      if (success) {
        setShowConfirmation(false)
        setActiveItem(undefined)
      }
      return success
    }
  }

  const menuButtonOptions: MenuButtonOption[] = []
  if (act != null && act.canCreateChild >= canCreateChild.Mixed && perms.Enrollment_Edit) {
    menuButtonOptions.push({
      id: 1,
      name: 'Add New Child',
      warning: '',
      onClick: function (): void {
        setShowAddChild(true)
      }
    })
  }
  if (enrollmentSearchData.statuses.every(s => s === 'processing')) {
    menuButtonOptions.push({
      id: 2,
      name: 'Mass Confirm',
      warning: 'Enrolling these children will be done in order of their corresponding federal poverty level. This action will fill all available seats with active enrollments. Any children that are left over will be added to your capacity waitlist.  Do you wish to continue?',
      onClick: function (): void {
        showCheckboxes(ActionType.MassEnroll)
      },
      onConfirm: handleConfirmMassEnrollment,
      onCancel: hideCheckboxes
    })
  }
  if (enrollmentSearchData.statuses.every(s => s === 'enrolled')) {
    menuButtonOptions.push({
      id: 3,
      name: 'Mass Start',
      warning: 'Please enter values and confirm the mass start.',
      onClick: function (): void {
        showCheckboxes(ActionType.MassStart)
      },
      onConfirm: handleConfirmMassStart,
      onCancel: hideCheckboxes
    })
  }

  if (enrollmentSearchData.statuses.every(s => s === 'started' || s === 'enrolled')) {
    menuButtonOptions.push({
      id: 4,
      name: 'Mass Exit',
      warning: 'Please enter values and confirm the mass exit.',
      onClick: function (): void {
        showCheckboxes(ActionType.MassExit)
      },
      onConfirm: handleConfirmMassExit,
      onCancel: hideCheckboxes
    })
  }

  function onDialogClose (event: any, reason: string): void {
    if (reason !== 'backdropClick') {
      setShowConfirmation(false)
    }
  }

  return (<>
    <Grid container sx={{ flexDirection: 'row' }}>
      <Grid item>
        <Typography variant="h3" data-testid="page-header">{enrollmentName} Enrollments</Typography>
      </Grid>
      {menuButtonOptions.length > 0 && (
        <Grid item sx={{ marginLeft: 'auto' }} className='circle-menu'>
          <MenuButton
            onContinue={handleContinueClick}
            options={menuButtonOptions}
            activeSelection={activeItem}
            setActiveSelection={setActiveItem}
          />
        </Grid>
      )}
    </Grid>
    <Dialog
      open={showConfirmation}
      data-testid="confirmationModal"
      onClose={onDialogClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle data-testid="modal-title" id="alert-dialog-title">
        {'Confirmation'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <ErrorDisplay fieldName='Mass Exit' />
          {warning}
          {(currentAction === ActionType.MassEnroll ? deliveryScheduleSelection : (currentAction === ActionType.MassExit ? exitDialogContent : (currentAction === ActionType.MassStart ? massStartDialogContent : undefined)))}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setShowConfirmation(false) }}>Cancel</Button>
        <Button data-testid="confirmation" onClick={handleConfirm} autoFocus>
          Ok
        </Button>
      </DialogActions>
    </Dialog>

    <Breadcrumbs maxItems={1} aria-label="breadcrumb">
      <Link sx={{ cursor: 'pointer' }} underline="hover" color="inherit" onClick={() => { nav(-1) }} >
        Return to {returnText ?? 'Enrollments'}
      </Link>
    </Breadcrumbs>

    <EnrollmentSearch
      key={refresh} // causes it to refresh when this value is updated
      current={enrollmentSearchData}
      onChange={handleFilter}
    />

    <div>
      <Box sx={{ width: '100%', marginTop: '20px', borderRadius: '16px', background: '#fff' }}>
        <DataTable
          page={pagination.page}
          name='exitedlistEnrollments'
          data-testid='exitedlistEnrollments'
          columns={columns}
          rows={pagination.internalRows}
          totalRecords={pagination.recordCount}
          loading={isRequestLoading}
          onSortChange={(col, sorts) => {
            pagination.onSortChange(col, sorts[0].order)
          }}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          onRowClick={showCheckBox ? () => { } : rowClick}
          initialColumnSorts={[new ColumnSort('childName')]}
          hasCheckbox={showCheckBox}
          initialCheckedItems={massEnrollmentList}
          onRowCheckboxChange={handleRawCheckboxChange}
          onHeaderCheckboxChange={handleSelectAllChange}
        />
      </Box>
    </div>

    {showAddChild && <NewChildValuesModal
      serviceID={Number(serviceId)}
      serviceName={enrollmentName}
      onCancel={() => { setShowAddChild(false) }}
    />
    }
  </>)
}
